import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { Badge } from '@common/Badge/Badge';
import { IconID } from '@common/Icon/Icon';
import { InfoBox } from '@common/InfoBox/InfoBox';
import { LengthCode } from '@icommerce/units';
import { LinkBox } from '@common/LinkBox/LinkBox';
import type { Teasers } from '@generated/graphql';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { ProductAttribute, ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAmount } from '@libs/attribute.helper';
import { chainEffectiveLengthCalculator, chainNumLinksCalculator, getBuyBoxConfiguration } from '@libs/buy-box';
import { enhanceCfCaseProductBoxPropsWithPrices } from '@libs/cfCase.helper';
import type { DeliveryTime } from '@libs/constants';
import { getLocale, ROUTES } from '@libs/constants';
import {
  getNewProductAndModelForPhaseOutProducts,
  getProductDescription,
  getProductIdForLocale,
  getProductTitle,
  getProductUsp,
} from '@libs/product.helper';
import { WidenUtil } from '@libs/widen-utils';
import { ProductHelper } from '@models/graphql';
import { WidenImageConfig } from '@models/widen-image-config';
import type { AlternativeProductProps } from '@product/AlternativeProduct/AlternativeProduct';
import type { BuyBoxProps } from '@product/BuyBox/Default/BuyBox';
import { DecisionToolsBox, LifetimeCalculatorModes } from '@product/DecisionToolsBox/DecisionToolsBox';
import { MarketingTeaserBox } from '@product/MarketingTeaserBox/MarketingTeaserBox';
import { ProductBox } from '@product/ProductBox/ProductBox';
import { ProductMediaCarousel } from '@product/ProductMediaCarousel/ProductMediaCarousel';
import { ProductNote } from '@product/ProductNote/ProductNote';
import { extractCFCaseProps } from '@product/ProductPage/PageLayouts/extractCFCaseProps';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import { ProductsCarousel } from '@product/ProductsCarousel/ProductsCarousel';
import { ProductTabs } from '@product/ProductTabs/ProductTabs';
import { ProductUsp } from '@product/ProductUsp/ProductUsp';
import type { SuitableProductProps } from '@product/SuitableProduct/SuitableProduct';
import { VariantSelector } from '@product/VariantSelector/VariantSelector';
import type { SharedProductPageProps } from '../ProductPage';
import { usePriceCountry } from '@category/CategoryPage/hooks/usePriceCountry';
import { renderBuyBoxLoadPlaceholder } from '@product/BuyBox/Default/BuxBoxPlaceholder';
import { convertProductAttributeValueAmountUnit } from '@libs/units';
import { UnitSystem } from '@igus/echain-shared-i18n';
import { useAppDataStore } from '@hooks/states/useAppDataStore';
import Locale from '@formatjs/intl-locale';
import { EXTERNAL_LINKS } from '@libs/redirect.helper';

const BuyBoxWithNoSSR = dynamic<BuyBoxProps>(() => import('@product/BuyBox/Default/BuyBox').then(mod => mod.BuyBox), {
  ssr: false,
  loading: renderBuyBoxLoadPlaceholder,
});

export interface DefaultProductPageLayoutProps extends SharedProductPageProps {
  suitableProducts: SuitableProductProps[];
  marketingTeasers: Teasers[];
  alternativeProducts: AlternativeProductProps[];
}

export const DefaultProductPageLayout: React.FC<DefaultProductPageLayoutProps> = ({
  alternativeProducts,
  carouselImages,
  dailyCopperPriceDefinition,
  marketingTeasers,
  prices,
  suitableProducts,
}) => {
  const { t } = useTranslation([
    'category/productTeaserPriceBox',
    'product/productDetails',
    'product/eSkinInformation',
  ]);

  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const isProductDiscontinued = ProductHelper.isDiscontinued(currentProduct);
  const isProductNew = ProductHelper.isNew(currentProduct);
  const isProductStandard = ProductHelper.isStandard(currentProduct);
  const priceData = useProductPageStore(state => state.priceData);
  const buyBoxReady = useProductPageStore(state => state.buyBoxReady);
  const usp = getProductUsp(currentProduct);
  const priceCountry = usePriceCountry();

  const successorProduct = getNewProductAndModelForPhaseOutProducts(currentProduct);

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  const { currentCategoryTree } = useAppDataStore.getState();

  const cfCasesOfAllProducts = useMemo(() => {
    if (isLoadingUniqueAssociatedProducts || !uniqueAssociatedProducts) {
      return [];
    }
    const { cfCases: cfs } = extractCFCaseProps(currentProduct, uniqueAssociatedProducts);
    return cfs;
  }, [isLoadingUniqueAssociatedProducts, currentProduct, uniqueAssociatedProducts]);

  const unitSystem = useProductPageStore(state => state.unitSystem);

  const cfCaseProductBoxProps = useMemo(() => {
    if (cfCasesOfAllProducts.length <= 0) {
      return null;
    }

    // MAT0012614 - Chainflex Case M
    const preferredCfCase =
      cfCasesOfAllProducts?.find(cfCase => cfCase.sku === 'MAT0012614') ??
      cfCasesOfAllProducts[cfCasesOfAllProducts.length - 1];

    return enhanceCfCaseProductBoxPropsWithPrices(preferredCfCase, prices);
  }, [cfCasesOfAllProducts, prices]);

  const productNoteHtml: string = getAttributeValue<string>(
    currentProduct.attributes?.product_notes as ProductAttribute<string>
  );

  const isESkin: boolean = getAttributeValue<boolean>(
    currentProduct.attributes?.only_with_e_skin_bool as ProductAttribute<boolean>
  );

  const eSkinConfiguratorImageUrl = WidenUtil.getWidenCDNUrl(
    new WidenImageConfig('m4ccrmayu1', 'CF_GEN_cf_clean_e-skin_flat_v1.webp', 300, 300)
  );
  let lifetimeCalculatorMode: LifetimeCalculatorModes = LifetimeCalculatorModes.NONE;
  if (
    getAttributeValue<boolean>(
      currentProduct.attributes?.release_of_service_life_calculation_CSL_bool as ProductAttribute<boolean>
    )
  ) {
    lifetimeCalculatorMode = LifetimeCalculatorModes.CHAINFLEX;
  }
  if (
    getAttributeValue<boolean>(
      currentProduct.attributes?.release_cable_for_readycable_product_finder_bool as ProductAttribute<boolean>
    )
  ) {
    lifetimeCalculatorMode = LifetimeCalculatorModes.READYCABLE;
  }

  // chainLinkDivision is only relevant for echain products, it is called 'teilung' in the tools cosmos
  let chainLinkDivision: number = null;
  if (unitSystem === UnitSystem.metric && currentProduct?.attributes?.pitch_e_chain) {
    chainLinkDivision = getAttributeValueAmount(
      getAttributeValue<ProductAttributeValueAmountUnit>(currentProduct.attributes?.pitch_e_chain),
      unitSystem
    );
  } else if (unitSystem === UnitSystem.imperial && currentProduct?.attributes?.pitch_e_chain) {
    // we need to convert the imperial value of pitch_e_chain from inch to feet, because we calculate the chainlength in feet
    chainLinkDivision = convertProductAttributeValueAmountUnit(
      getAttributeValue<ProductAttributeValueAmountUnit>(currentProduct.attributes?.pitch_e_chain),
      LengthCode.FEET,
      false
    );
  }

  const buyBoxConfiguration = getBuyBoxConfiguration(
    priceData.priceType,
    unitSystem,
    t,
    new Locale(locale),
    priceCountry,
    currentCategoryTree,
    chainLinkDivision
  );

  return (
    <>
      <h1 className="my-10 leading-5xl">{getProductTitle(currentProduct)}</h1>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center igus-text-styles main-content-grid"
        data-testid="page-layout-default"
      >
        <div>
          {isProductNew && <Badge text={t('PRODUCT_DETAILS.NEW_PRODUCT_BADGE', { ns: 'product/productDetails' })} />}
          {isProductStandard && !isProductNew && (
            <Badge text={t('PRODUCT_DETAILS.STANDARD_PRODUCT_BADGE', { ns: 'product/productDetails' })} />
          )}
          {isProductDiscontinued && (
            <Badge text={t('PRODUCT_DETAILS.DISCONTINUED_BADGE', { ns: 'product/productDetails' })} />
          )}
          {carouselImages?.length > 0 && <ProductMediaCarousel media={carouselImages} />}
        </div>
        <ProductUsp usp={usp} />
        <div className="w-[calc(100%_-_4rem)]">
          <VariantSelector />
          {(isProductDiscontinued || successorProduct) && (
            <InfoBox
              text={
                t('PRODUCT_DETAILS.DISCONTINUED_TEXT', { ns: 'product/productDetails' }) +
                (successorProduct
                  ? ` ${t('PRODUCT_DETAILS.NEW_PRODUCT_TEXT', {
                      link: ROUTES.PRODUCT.DETAILS(successorProduct.modelId, successorProduct.productId),
                      ns: 'product/productDetails',
                    })}`
                  : '')
              }
            />
          )}
          {productNoteHtml && <ProductNote html={productNoteHtml} />}
          {cfCaseProductBoxProps && (
            <ProductBox
              sku={cfCaseProductBoxProps.sku}
              title={t('PRODUCT_DETAILS.CF_CASE.TITLE', { ns: 'product/productDetails' })}
              image={cfCaseProductBoxProps.image}
              pricePerUnit={cfCaseProductBoxProps.pricePerUnit}
              currencyCode={cfCaseProductBoxProps.currencyCode}
              linkTargetOverride="/info/chainflex-case"
              externalLink
            />
          )}
          {isESkin && (
            <LinkBox
              title={t('E_SKIN_INFORMATION.CONFIGURATOR.LINK_TITLE', { ns: 'product/eSkinInformation' })}
              description={t('E_SKIN_INFORMATION.CONFIGURATOR.LINK_DESCRIPTION', { ns: 'product/eSkinInformation' })}
              imageUrl={eSkinConfiguratorImageUrl}
              text={t('E_SKIN_INFORMATION.CONFIGURATOR.LINK_REFERENCE', { ns: 'product/eSkinInformation' })}
              icon={IconID.ARROW_RIGHT}
              iconPrimary
              href={EXTERNAL_LINKS.E_SKIN_CONFIGURATOR(locale)}
              onClick={() => {}}
            />
          )}
        </div>
        <div className="w-full">
          {!buyBoxReady && renderBuyBoxLoadPlaceholder()}
          {buyBoxReady && buyBoxConfiguration !== undefined && (
            <BuyBoxWithNoSSR
              chainLenghtCalculator={chainLinkDivision ? chainEffectiveLengthCalculator(chainLinkDivision) : undefined}
              chainNumLinksCalculator={chainLinkDivision ? chainNumLinksCalculator(chainLinkDivision) : undefined}
              sku={getProductIdForLocale(currentProduct, locale)}
              iproPrgrID={getAttributeValue<string>(
                currentProduct.attributes?.attr_ipro_PrgrID as ProductAttribute<string>
              )}
              iproProdID={getAttributeValue<string>(
                currentProduct.attributes?.attr_ipro_ProdID as ProductAttribute<string>
              )}
              copperIndex={getAttributeValue<ProductAttributeValueAmountUnit>(
                currentProduct.attributes
                  ?.attr_copper_index_metric_kg_km as ProductAttribute<ProductAttributeValueAmountUnit>
              )}
              dailyCopperPriceDefinition={dailyCopperPriceDefinition}
              deliveryTimeCode={
                currentProduct.delivery_times
                  ? (currentProduct.delivery_times[0]?.code?.toString() as DeliveryTime)
                  : undefined
              }
              deliveryTimeText={
                currentProduct.delivery_times && currentProduct.delivery_times[0]?.label
                  ? currentProduct.delivery_times[0]?.label
                  : undefined
              }
              showAddToEchainConfiguration={getAttributeValue<boolean>(
                currentProduct.attributes?.add_to_echain_configuration as ProductAttribute<boolean>
              )}
              isEskinProduct={getAttributeValue<boolean>(
                currentProduct.attributes?.only_with_e_skin_bool as ProductAttribute<boolean>
              )}
              buyBoxConfiguration={buyBoxConfiguration}
            />
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 place-content-start justify-items-center product-tabs-grid">
        <div className="w-full lg:col-span-3 order-last lg:order-first">
          <ProductTabs productDetails={getProductDescription(currentProduct)} suitableProducts={suitableProducts} />
        </div>
        <div>
          {currentProduct?.id !== 'MAT0012614' && (
            <DecisionToolsBox
              showExpertSystem={
                !!getAttributeValue<boolean>(
                  currentProduct.attributes?.igubal_expert_release_bool as ProductAttribute<boolean>
                )
              }
              showEplan={!!getAttributeValue<boolean>(currentProduct.attributes?.e_plan as ProductAttribute<boolean>)}
              showSampleBox={getAttributeValue<boolean>(
                currentProduct.attributes?.sample_ordering_allowed_bool as ProductAttribute<boolean>
              )}
              lifetimeCalculatorMode={lifetimeCalculatorMode}
              showZuken={getAttributeValue<boolean>(
                currentProduct.attributes?.zuken_release_bool as ProductAttribute<boolean>
              )}
              showCAD={getAttributeValue<boolean>(
                currentProduct.attributes?.cad_data_bool as ProductAttribute<boolean>
              )}
              showLifeTimeBearingMode={getAttributeValue<boolean>(
                currentProduct.attributes
                  ?.release_of_service_life_calculation_ball_bearing_bool as ProductAttribute<boolean>
              )}
              showRoleConfigurator={getAttributeValue<boolean>(
                currentProduct.attributes?.release_of_roler_configurator_bool as ProductAttribute<boolean>
              )}
              showLeadScrewConfigurator={getAttributeValue<boolean>(
                currentProduct.attributes?.release_drylin_leadscrew_configurator as ProductAttribute<boolean>
              )}
              variantSku={currentProduct.id}
              description={getProductTitle(currentProduct)}
              disabled={ProductHelper.isDiscontinued(currentProduct)}
              quantity={1}
              length={buyBoxConfiguration.defaultLength}
            />
          )}
          {marketingTeasers?.length > 0 && (
            <div className="mt-12 grid gap-4 lg:gap-0 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 marketing-teaser-boxes">
              {marketingTeasers.map((teaser, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <span className="mt-8" key={index}>
                  <MarketingTeaserBox teaser={teaser} />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {alternativeProducts.length > 0 && (
        <>
          <h2 className="bg-igus-primary text-white font-medium px-8 py-4 mt-4">
            {t('PRODUCT_DETAILS.OTHER_SOLUTIONS', { ns: 'product/productDetails' })}
          </h2>
          <div className="grid grid-cols-1 gap-8 justify-items-center igus-text-styles main-content-grid">
            <ProductsCarousel products={alternativeProducts} activeIndex={0} />
          </div>
        </>
      )}
    </>
  );
};
